/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useRef, useState } from 'react';

// Mantine
import {
	Box,
	Drawer,
	Skeleton,
	Stack,
	Text,
	Title,
} from '@mantine/core';

// Styles
import clsx from 'clsx';
import classes from './DrawerMintHistoryDetails.module.css';

// Components
import { Button } from '@components/atoms/Button';
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { Stepper } from '@components/molecules/Stepper';

// I18n
import { useTranslation } from 'react-i18next';

// Configs
import { STEPS } from '../../../productions/components/StartMint/StartMint.config';

// Store
import useStore from '@store/index';

// Router
import { useLocation } from 'react-router';

// Api
import { useAco2TokenById } from '../../api/get-mint-history-by-id';

// Mantine
import {
	ActionIcon,
	Group,
	Highlight,
} from '@mantine/core';

// Components
import { Datatable } from '@components/organisms/Datatable';

// Datatable config
import { tableSchemaBase as columns } from './mint-history-productions.columns';

// Utils
import {
	formatDate,
	handleOpenTransactionExternalLink,
} from '@utils/functions';

import dayjs from 'dayjs';

// Store
import { MintStatus } from '@components/atoms/MintStatus';
import { TProduction } from '@global-types/index';

import { NOT_APPLICABLE } from '@config/constants/common';
import { ENUM_UNITS } from '@config/constants/units';
import { DrawerProduction } from '@features/sourcing/productions/components/DrawerProduction';
import { IconExternalLink } from '@tabler/icons-react';
import { formatNumeric } from '@utils/formats';
import {
	getLabelFromValue,
	getMintStatusColor,
} from '@utils/mint-status';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import sum from 'lodash/sum';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DrawerMintHistoryDetails = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** HOOKS *********************************** */
	const [openDrawer, setOpenDrawer] = useState(false);

	const viewport = useRef<HTMLDivElement>(null);

	const location = useLocation();

	const { t } = useTranslation();

	const {
		setProductionsStartMintsActiveStep,
		productionStartMintsActiveStep,
		productionStartMintsSelectedRecords,
		setProductionsStartMintsSelectedRecords,
		setProductionsStartMintsProducer,
		setProductionsStartMintsQueryParams,
		productionStartMintsQueryParams,
		mintHistorySelected,
		productionStartMintsSortStatus,
		setProductionsStartMintsSelectedRow,
		productionStartMintsSelectedRow,
	} = useStore();

	const { data, blockchain, transactionHash, isPending } =
		useAco2TokenById({
			id: mintHistorySelected.id,
		});

	/* ******************************** HELPERS ********************************* */

	const handleCloseDrawerMintHistoryDetails = () => {
		props.onClose();
		setProductionsStartMintsProducer('');
		setProductionsStartMintsSelectedRecords([]);
		setProductionsStartMintsActiveStep(0);
	};

	/* ******************************** VARIABLES ******************************* */

	const TotalTokenEquivalents = isArray(data)
		? sum(
				data.map((item) => {
					if (isNil(item.numericTokenEquivalent)) {
						return 0;
					}
					return item.numericTokenEquivalent;
				})
			)
		: 0;

	/* ******************************** APIs ******************************* */

	const handleOpenURL = () => {
		if (transactionHash)
			handleOpenTransactionExternalLink(
				transactionHash || ''
			);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<>
			<Drawer.Root
				position="right"
				size="100%"
				opened={props.opened}
				onClose={handleCloseDrawerMintHistoryDetails}
			>
				<Drawer.Overlay />
				<Drawer.Content
					ref={viewport}
					className={classes.root}
				>
					{/** Header */}
					<Drawer.Header className={classes.DrawerHeader}>
						<Box className={classes.header}>
							<PreviousDrawerButton
								onClick={
									handleCloseDrawerMintHistoryDetails
								}
							>
								{Object.keys(mintHistorySelected)
									.length !== 0 ||
								location.pathname ==
									'/productions/mint-history'
									? t(
											'mintHistories.backToAllMintHistories'
										)
									: t('productions.backToAllProductions')}
							</PreviousDrawerButton>{' '}
						</Box>
					</Drawer.Header>
					{/** Body */}
					<Drawer.Body className={classes.body}>
						<Box className={classes.containerTitle}>
							<Box className={classes.contentTitle}>
								<Title className={classes.titleForm}>
									{t('productions.title')}
								</Title>
								<Text className={classes.subTitleForm}>
									/ {t('productions.mintProduction')}
								</Text>
							</Box>
						</Box>
						{/** Stepper */}
						<Box className={classes.containerStepper}>
							<Box className={classes.wrapperStepper}>
								<Stepper
									readOnly
									options={STEPS}
									active={3}
									iconSize={32}
									onStepClick={() => {}}
								/>
							</Box>
						</Box>
						<Box className={classes.wrapperBody}>
							{isPending ? (
								<Stack w={'100%'}>
									{Array.from(Array(10).keys()).map(
										(index) => (
											<Skeleton
												key={index}
												height={50}
												width={'100%'}
												radius="sm"
											/>
										)
									)}
								</Stack>
							) : (
								<Box className={classes.container}>
									{/*Drawer production */}
									<DrawerProduction
										opened={openDrawer}
										handleverify={() => {}}
										handledelete={() => {}}
										handlestartmint={() => {}}
										onClose={() => {
											setOpenDrawer(false);
										}}
										position={'right'}
										data={{
											...(productionStartMintsSelectedRow ||
												{}),
											blockchain:
												blockchain || NOT_APPLICABLE,
											transactionHash:
												transactionHash || NOT_APPLICABLE,
										}}
										fromstepper
									/>

									<Box className={classes.content}>
										<Highlight
											highlight={[
												'highlighted',
												'' +
												isNumber(TotalTokenEquivalents)
													? formatNumeric(
															TotalTokenEquivalents
														)
													: '0',
											]}
											highlightStyles={{
												backgroundImage:
													'linear-gradient(45deg, #647FE8, #647FE8)',
												fontWeight: 700,
												WebkitBackgroundClip: 'text',
												WebkitTextFillColor:
													'transparent',
											}}
											c="var(--mantine-color-dark)"
											fw={500}
											className={classes.title}
										>
											{data?.length +
												` ${t(
													'productions.productionsMintedWithAnEquivalentOf'
												)} ${
													TotalTokenEquivalents &&
													isNumber(TotalTokenEquivalents)
														? formatNumeric(
																TotalTokenEquivalents
															)
														: 0
												} ${ENUM_UNITS.tco2e}`}
										</Highlight>
										<Box
											className={
												classes.containerMintInformations
											}
										>
											<Box
												className={
													classes.colMintInformation
												}
											>
												<Text
													className={
														classes.mintInformartionTitle
													}
												>
													{t('global.mintDate')}
												</Text>
												<Text
													className={
														classes.mintInformartionDate
													}
												>
													{mintHistorySelected &&
													mintHistorySelected.createdAt &&
													dayjs(
														mintHistorySelected.createdAt
													).isValid()
														? `${formatDate(
																mintHistorySelected.createdAt,
																'DD/MM/YYYY'
															)}`
														: `${formatDate(dayjs(), 'DD/MM/YYYY')}`}
												</Text>
											</Box>
											<Box
												className={
													classes.borderColMintInformation
												}
											/>
											<Box
												className={
													classes.colMintInformation
												}
											>
												<Text
													className={
														classes.mintInformartionTitle
													}
												>
													{t('global.mintStatus')}
												</Text>

												{mintHistorySelected &&
												mintHistorySelected.status ? (
													<MintStatus
														children={getLabelFromValue(
															mintHistorySelected &&
																mintHistorySelected.status &&
																mintHistorySelected.status
														)}
														type={getMintStatusColor(
															mintHistorySelected &&
																mintHistorySelected.status &&
																mintHistorySelected.status
														)}
													/>
												) : (
													<Text
														className={
															classes.notApplicable
														}
													>
														{NOT_APPLICABLE}
													</Text>
												)}
											</Box>
											<Box
												className={
													classes.borderColMintInformation
												}
											/>
											<Box
												className={
													classes.colMintInformation
												}
											>
												<Group gap={'xs'}>
													<Text
														className={
															classes.mintInformartionTitle
														}
													>
														{`${t('global.blockchain')} :`}
													</Text>
													<Text
														className={
															classes.mintInformartionTitle
														}
													>
														{blockchain
															? blockchain
															: NOT_APPLICABLE}
													</Text>
												</Group>
												<Group gap={'xs'}>
													<Text
														className={
															classes.mintInformartionLabelTokenId
														}
													>
														{`${t('global.tokenId')}`}
													</Text>
													{transactionHash && (
														<ActionIcon
															size={'xs'}
															onClick={() => {}}
															variant="transparent"
														>
															<IconExternalLink
																onClick={handleOpenURL}
																className={
																	classes.mintInformartionTokenId
																}
																size={'15px'}
															/>
														</ActionIcon>
													)}
													<Text
														className={clsx(
															classes.mintInformartionTokenId,
															!transactionHash &&
																classes.mintInformartionTokenIdNotApplicable
														)}
														onClick={handleOpenURL}
													>
														:
														{` ${
															!transactionHash
																? NOT_APPLICABLE
																: transactionHash
														}`}
													</Text>
												</Group>
											</Box>
										</Box>
										<Box
											className={classes.contentDatatable}
										>
											<Datatable
												columns={columns}
												data={data}
												onRowClick={() => {}}
												onViewMore={(
													row: TProduction
												) => {
													setProductionsStartMintsSelectedRow(
														row
													);
													setOpenDrawer(true);
												}}
												style={{
													minHeight: '100%',
												}}
												height={250}
												selectedRecords={null}
												sortstatus={
													productionStartMintsSortStatus
												}
											/>
										</Box>
									</Box>
								</Box>
							)}
						</Box>
					</Drawer.Body>
					{/** Footer */}
					<Box className={classes.containerFooter}>
						<Box className={classes.spacerFooter} />
						<Box className={classes.bodyFooter}>
							<Box
								className={classes.containerButtonsFooter}
							>
								<Box
									onClick={() => {
										if (
											productionStartMintsActiveStep > 0
										)
											setProductionsStartMintsActiveStep(
												productionStartMintsActiveStep - 1
											);
										if (
											productionStartMintsSelectedRecords.length ==
											0
										)
											setProductionsStartMintsQueryParams(
												{
													...productionStartMintsQueryParams,
													project: '',
												}
											);
									}}
									className={clsx(
										classes.previousButton,
										productionStartMintsActiveStep == 0 &&
											classes.previousButtonDisabled
									)}
								>
									{productionStartMintsActiveStep !== 3 &&
										t('global.previous')}
								</Box>
							</Box>
							<Box
								className={classes.containerButtonsFooter}
							>
								<Button
									fullWidth
									hiddenFrom="xxl"
									size="md"
									onClick={
										handleCloseDrawerMintHistoryDetails
									}
								>
									{t('global.ok')}
								</Button>

								<Button
									fullWidth
									visibleFrom="xxl"
									size="lg"
									type="submit"
									onClick={
										handleCloseDrawerMintHistoryDetails
									}
								>
									{t('global.ok')}
								</Button>
							</Box>
						</Box>

						<Box className={classes.spacerFooter} />
					</Box>
				</Drawer.Content>
			</Drawer.Root>
		</>
	);
};
